import { ErrorOption } from "react-hook-form";
import { ApiResponse } from "./dtos";
import { ErrorType, State } from "./types";
import moment from "moment";
import { StatusCodes } from "http-status-codes";
import { Coordinate, Event, Location, Address, Pricing } from "./entities";
import { OrganizationUser } from "./entities";

export const isBrowser = () => typeof window !== 'undefined';
export const hasName = (user: OrganizationUser) =>
  Boolean(user.firstName || user.lastName);

export const getName = (user: OrganizationUser) =>
  `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim();

export const combineErrors = (errors?: string[], separator: string = ", ") =>
  errors?.join(separator);

export const getApiError = (response: ApiResponse): ErrorOption => {
  if (!response.isSuccess && !response.errors) {
    return {
      type: "serverError",
      message:
        response.statusCode == StatusCodes.INTERNAL_SERVER_ERROR
          ? "There was an issue processing your request."
          : response.message,
    };
  }

  return {
    type: "serverError",
    message: combineErrors(response.errors),
  };
};

export const getError = (type: ErrorType, errors: string[]): ErrorOption => ({
  type: type,
  message: combineErrors(errors),
});

export const hasErrors = (response: ApiResponse) => {
  return (
    !response.isSuccess ||
    (Boolean(response.errors) && response.errors!.length > 0)
  );
};

export const getGoogleDirectionsUrl = (from?: Coordinate, to?: Coordinate) =>
  `https://www.google.com/maps/dir/'${from ? `${from.latitude},${from.longitude}` : ''}'/'${to?.latitude},${to?.longitude}'`;

export const getGoogleMapUrl = (coord: Coordinate) =>
  `https://www.google.com/maps/search/${coord.latitude},${coord.longitude}`;

export const getEventShareUrl = (id: string) => {
  const baseUrl =
    typeof window !== "undefined"
      ? window.location.origin
      : import.meta.env.PUBLIC_ENV__HOSTED_DOMAIN;
  return `${baseUrl}?event=${id}`;
};

export const getOrganizationShareUrl = (id: string) => {
  const baseUrl =
    typeof window !== "undefined"
      ? window.location.origin
      : import.meta.env.PUBLIC_ENV__HOSTED_DOMAIN;
  return `${baseUrl}?organization=${id}`;
};

type AddressLike = {
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string | null;
  rawAddress?: string | null;
  lookupFormat?: string | null;
  textDisplay?: string | null;
  htmlDisplay?: string | null;
};

export const formatAddressText = (
  address?: Address | AddressLike | string | null,
  options: { includeCountry?: boolean } = { includeCountry: true }
) => {
  if (!address) return "";
  if (typeof address === "string") return address.trim();

  const includeCountry = options.includeCountry !== false;
  const country = address.country || "";

  let addressText =
    address.textDisplay?.trim().replace(/\n/g, ", ") ||
    address.rawAddress ||
    [
      address.addressLine1,
      address.addressLine2,
      [address.city, address.state, address.zip].filter(Boolean).join(" "),
    ]
      .filter(Boolean)
      .join(", ");

  if (includeCountry && country && !addressText.includes(country)) {
    addressText = addressText ? `${addressText}, ${country}` : country;
  }

  return addressText.trim();
};

export const downloadFile = (filename: string, blob: Blob) => {
  const url = window.URL.createObjectURL(blob);

  const element = document.createElement("a");
  element.style.display = "none";
  element.href = url;
  element.download = filename;
  document.body.appendChild(element);

  element.click();

  document.body.removeChild(element);
  window.URL.revokeObjectURL(url);
};

export const isAuthUrlPath = (urlPath: string) =>
  ["/login", "/forgot-password", "/register"].indexOf(
    urlPath?.toLocaleLowerCase()
  ) >= 0;
export const isAccountUrlPath = (urlPath: string) =>
  urlPath?.toLowerCase()?.startsWith("/account");

export const pathJoin = (parts: Array<unknown | never>, sep: string) => {
  const separator = sep || "/";
  const replace = new RegExp(separator + "{1,}", "g");
  return parts.join(separator).replace(replace, separator);
};

export const formatDate = (date?: moment.Moment | Date) => {
  if (!date) return ``;

  return moment(date).format("MMMM Do, YYYY h:mm a");
};

export const formatEventDateRange = (
  start?: moment.Moment | Date,
  end?: moment.Moment | Date
) => {
  if (!start || !end) {
    return {
      full: formatDate(start) || formatDate(end) || "",
      isOneDay: false,
    };
  }

  const mStart = moment(start);
  const mEnd = moment(end);

  const isOneDay =
    mStart.date() === mEnd.date() &&
    mStart.month() === mEnd.month() &&
    mStart.year() === mEnd.year();

  if (isOneDay) {
    // Same day: "Wednesday, January 8th, 2026 6pm - 8pm"
    return {
      date: mStart.format("dddd, MMMM Do, YYYY"),
      time: `${mStart.format("h:mma")} - ${mEnd.format("h:mma")}`,
      isOneDay: true,
      full: `${mStart.format("dddd, MMMM Do, YYYY")} ${mStart.format("h:mma")} - ${mEnd.format("h:mma")}`,
    };
  }

  // Multi-day: Display dates and times on separate lines
  return {
    isOneDay: false,
    startDate: mStart.format("dddd, MMMM Do, YYYY"),
    startTime: mStart.format("h:mma"),
    endDate: mEnd.format("dddd, MMMM Do, YYYY"),
    endTime: mEnd.format("h:mma"),
    full: `${mStart.format("dddd, MMMM Do, YYYY")}\n${mStart.format("h:mma")}\n${mEnd.format("dddd, MMMM Do, YYYY")}\n${mEnd.format("h:mma")}`,
  };
};

export const getStates = (country?: 'US' | 'CA'): State[] => {
  const usStates: State[] = [
    { name: "Alabama", id: "AL", country: "US" },
    { name: "Alaska", id: "AK", country: "US" },
    { name: "Arizona", id: "AZ", country: "US" },
    { name: "Arkansas", id: "AR", country: "US" },
    { name: "California", id: "CA", country: "US" },
    { name: "Colorado", id: "CO", country: "US" },
    { name: "Connecticut", id: "CT", country: "US" },
    { name: "Delaware", id: "DE", country: "US" },
    { name: "Florida", id: "FL", country: "US" },
    { name: "Georgia", id: "GA", country: "US" },
    { name: "Hawaii", id: "HI", country: "US" },
    { name: "Idaho", id: "ID", country: "US" },
    { name: "Illinois", id: "IL", country: "US" },
    { name: "Indiana", id: "IN", country: "US" },
    { name: "Iowa", id: "IA", country: "US" },
    { name: "Kansas", id: "KS", country: "US" },
    { name: "Kentucky", id: "KY", country: "US" },
    { name: "Louisiana", id: "LA", country: "US" },
    { name: "Maine", id: "ME", country: "US" },
    { name: "Maryland", id: "MD", country: "US" },
    { name: "Massachusetts", id: "MA", country: "US" },
    { name: "Michigan", id: "MI", country: "US" },
    { name: "Minnesota", id: "MN", country: "US" },
    { name: "Mississippi", id: "MS", country: "US" },
    { name: "Missouri", id: "MO", country: "US" },
    { name: "Montana", id: "MT", country: "US" },
    { name: "Nebraska", id: "NE", country: "US" },
    { name: "Nevada", id: "NV", country: "US" },
    { name: "New Hampshire", id: "NH", country: "US" },
    { name: "New Jersey", id: "NJ", country: "US" },
    { name: "New Mexico", id: "NM", country: "US" },
    { name: "New York", id: "NY", country: "US" },
    { name: "North Carolina", id: "NC", country: "US" },
    { name: "North Dakota", id: "ND", country: "US" },
    { name: "Ohio", id: "OH", country: "US" },
    { name: "Oklahoma", id: "OK", country: "US" },
    { name: "Oregon", id: "OR", country: "US" },
    { name: "Pennsylvania", id: "PA", country: "US" },
    { name: "Rhode Island", id: "RI", country: "US" },
    { name: "South Carolina", id: "SC", country: "US" },
    { name: "South Dakota", id: "SD", country: "US" },
    { name: "Tennessee", id: "TN", country: "US" },
    { name: "Texas", id: "TX", country: "US" },
    { name: "Utah", id: "UT", country: "US" },
    { name: "Vermont", id: "VT", country: "US" },
    { name: "Virginia", id: "VA", country: "US" },
    { name: "Washington", id: "WA", country: "US" },
    { name: "Washington, D.C.", id: "DC", country: "US" },
    { name: "West Virginia", id: "WV", country: "US" },
    { name: "Wisconsin", id: "WI", country: "US" },
    { name: "Wyoming", id: "WY", country: "US" },
  ];

  const canadianProvinces: State[] = [
    { name: "Alberta", id: "AB", country: "CA" },
    { name: "British Columbia", id: "BC", country: "CA" },
    { name: "Manitoba", id: "MB", country: "CA" },
    { name: "New Brunswick", id: "NB", country: "CA" },
    { name: "Newfoundland and Labrador", id: "NL", country: "CA" },
    { name: "Northwest Territories", id: "NT", country: "CA" },
    { name: "Nova Scotia", id: "NS", country: "CA" },
    { name: "Nunavut", id: "NU", country: "CA" },
    { name: "Ontario", id: "ON", country: "CA" },
    { name: "Prince Edward Island", id: "PE", country: "CA" },
    { name: "Quebec", id: "QC", country: "CA" },
    { name: "Saskatchewan", id: "SK", country: "CA" },
    { name: "Yukon", id: "YT", country: "CA" },
  ];

  if (country === 'US') {
    return usStates;
  } else if (country === 'CA') {
    return canadianProvinces;
  }

  return [...usStates, ...canadianProvinces];
};

/**
 * Parses an event from the backend JSON format to the frontend Event format.
 * Handles conversion of dateRange ISO strings to moment objects,
 * coordinate numbers, vendorId array, and other backend-specific formats.
 */
export const parseEventFromBackend = (backendEvent: any): Event => {
  const event = backendEvent as Event;

  // Backend stores UTC (correctly converted from event-location wall clock).
  // Display in viewer's device-local timezone throughout the UI.
  if (event.dateRange) {
    if (typeof event.dateRange.start === "string") {
      event.dateRange.start = moment.utc(event.dateRange.start).local();
    }
    if (typeof event.dateRange.end === "string") {
      event.dateRange.end = moment.utc(event.dateRange.end).local();
    }
  }

  // Ensure vendorId is an array (backend sends as array of numbers)
  if (backendEvent.vendorId !== undefined) {
    if (Array.isArray(backendEvent.vendorId)) {
      event.vendorId = backendEvent.vendorId;
    } else if (typeof backendEvent.vendorId === "number") {
      // Handle legacy single number format
      event.vendorId = [backendEvent.vendorId];
    } else {
      event.vendorId = [];
    }
  }

  // Parse location with coordinate numbers
  if (backendEvent.location) {
    const location = new Location();
    location.id = backendEvent.location.id || "";

    // Parse address
    if (backendEvent.location.address) {
      const address = new Address();
      Object.assign(address, backendEvent.location.address);
      location.address = address;
    }

    // Parse coordinate (backend sends latitude/longitude as numbers)
    if (backendEvent.location.coordinate) {
      const coordinate = new Coordinate();
      coordinate.latitude = backendEvent.location.coordinate.latitude;
      coordinate.longitude = backendEvent.location.coordinate.longitude;
      location.coordinate = coordinate;
    }

    event.location = location;
  }

  // Parse pricing array
  if (backendEvent.pricing && Array.isArray(backendEvent.pricing)) {
    event.pricing = backendEvent.pricing.map((p: any) => {
      const pricing = new Pricing(p.description, p.price);
      if (p.id) pricing.id = p.id;
      return pricing;
    });
  }

  // Parse user object (backend sends simplified user object)
  if (backendEvent.user) {
    event.user = {
      id: backendEvent.user.id || "",
      firstName: backendEvent.user.firstName || "",
      lastName: backendEvent.user.lastName || "",
      email: backendEvent.user.email || "",
      organizationMemberships: backendEvent.user.organizationMemberships || [],
    };
  }

  // Ensure venueId is a number
  if (backendEvent.venueId !== undefined) {
    event.venueId = typeof backendEvent.venueId === "number"
      ? backendEvent.venueId
      : parseInt(String(backendEvent.venueId), 10) || undefined;
  }

  // Parse venue data if available
  if (backendEvent.venue) {
    event.venue = backendEvent.venue;
  }

  return event;
};
